import type {
  CreateOrderRequest,
  OrderSummary,
  CreateOrderResponse,
  CreateOrUpdatePackageRequest,
  LicensePackageDetails,
} from "@xobcl/types";
import { config } from './config';
import { logger } from './logger';

// ============================================================================
// Type Definitions for Hosting Operator and Package APIs
// ============================================================================

/**
 * Request to transition an order to a new state (hosting operator).
 */
export interface TransitionOrderRequest {
  targetState: string;
  reason?: string | null;
  approvedQuantity?: number | null;
  operatorNotes?: string | null;
}

/**
 * Response after order state transition.
 */
export interface TransitionOrderResponse {
  orderId: string;
  state: string;
  transitionedAt: string;
  quantityApproved?: number | null;
  allowedTargetStates?: string[];
}

/**
 * Order as displayed in hosting operator queue.
 */
export interface OperatorQueueOrder extends OrderSummary {
  tenantId: string;
  tenantName?: string | null;
  timeInCurrentStateSLAHours?: number | null;
  allowedTargetStates?: string[];
}

// Reuse the auth header provider and apiFetch mechanism from api.ts
let authHeaderProvider: null | (() => Promise<string>) = null;

export function setOrderAuthHeaderProvider(fn: () => Promise<string>) {
  authHeaderProvider = fn;
}

const API_BASE_URL = config.api.baseUrl;

async function orderApiFetch<T>(path: string, options: RequestInit = {}, silentOn?: number[]): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };

  // Wait a bit for auth provider to be set up if not available yet
  let attempts = 0;
  while (!authHeaderProvider && attempts < 10) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }

  if (authHeaderProvider) {
    try {
      const token = await authHeaderProvider();
      logger.debug('Token acquired for order API call', { hasToken: !!token }, 'ORDER_API');
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch (err) {
      logger.error('Token acquisition failed for order API', err, 'ORDER_API');
      // ignore token fetch errors; let API return 401 which UI can handle
    }
  } else {
    logger.warn('No authHeaderProvider set for order API after waiting', undefined, 'ORDER_API');
  }

  // All Azure Functions need /api prefix
  const apiPath = `/api${path}`;
  const url = `${API_BASE_URL}${apiPath}`;
  logger.apiCall(options.method || 'GET', url, options.body ? JSON.parse(options.body as string) : undefined);

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include' // Important: This ensures SWA authentication headers are sent
  });

  logger.apiResponse(res.status, res.statusText, url);
  if (!res.ok) {
    // Try to parse error details from response body
    let errorMessage = `Order API error ${res.status}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If response is not JSON, use status text
      errorMessage = `Order API error ${res.status}: ${res.statusText}`;
    }

    // Only log error if not in silent status codes list
    if (!silentOn || !silentOn.includes(res.status)) {
      logger.apiError(url, errorMessage);
    }

    throw new Error(errorMessage);
  }
  return res.json() as Promise<T>;
}

/**
 * Create a new upgrade order for the current tenant.
 * @param tenantId - The tenant ID for which to create the order
 * @param request - Order creation request (packageId, quantityRequested, optional notes/description)
 * @returns Created order response with order ID and details
 * @throws Error if authorization fails or request validation fails
 */
export async function createOrderAsync(tenantId: string, request: CreateOrderRequest): Promise<CreateOrderResponse> {
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  if (!request.packageId) {
    throw new Error("Package ID is required");
  }
  if (request.quantityRequested <= 0) {
    throw new Error("Quantity must be greater than 0");
  }

  return orderApiFetch<CreateOrderResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/orders`,
    {
      method: "POST",
      body: JSON.stringify(request)
    }
  );
}

/**
 * Get order history for the current tenant.
 * @param tenantId - The tenant ID for which to retrieve order history
 * @returns Array of order summaries for the tenant (sorted by most recent first)
 * @throws Error if authorization fails
 */
export async function getTenantOrderHistoryAsync(tenantId: string): Promise<OrderSummary[]> {
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  return orderApiFetch<OrderSummary[]>(
    `/tenants/${encodeURIComponent(tenantId)}/orders`,
    { method: "GET" }
  );
}

/**
 * Get hosting operator queue of all orders across tenants.
 * @param filterState - Optional state to filter by (e.g., "under-review", "approved")
 * @returns Queue orders visible to hosting operator
 * @throws Error if authorization fails
 */
export async function getHostingQueueAsync(filterState?: string | null): Promise<OperatorQueueOrder[]> {
  const params = filterState ? `?state=${encodeURIComponent(filterState)}` : '';
  return orderApiFetch<OperatorQueueOrder[]>(
    `/portal/orders${params}`,
    { method: "GET" }
  );
}

/**
 * Transition an order to a new state (hosting operator).
 * @param tenantId - Tenant ID of the order
 * @param orderId - Order ID to transition
 * @param request - Transition details including target state
 * @returns Updated order state
 * @throws Error if authorization fails or transition is invalid
 */
export async function transitionOrderAsync(
  tenantId: string,
  orderId: string,
  request: TransitionOrderRequest
): Promise<TransitionOrderResponse> {
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  if (!orderId) {
    throw new Error("Order ID is required");
  }
  if (!request.targetState) {
    throw new Error("Target state is required");
  }

  return orderApiFetch<TransitionOrderResponse>(
    `/portal/orders/${encodeURIComponent(tenantId)}/${encodeURIComponent(orderId)}/transition`,
    {
      method: "PATCH",
      body: JSON.stringify(request)
    }
  );
}

/**
 * Get public package catalog (available to customers).
 * @returns List of active license packages
 * @throws Error if authorization or API call fails
 */
export async function getPackageCatalogAsync(): Promise<LicensePackageDetails[]> {
  return orderApiFetch<LicensePackageDetails[]>(
    '/packages',
    { method: "GET" }
  );
}

/**
 * Get specific package details.
 * @param packageId - Package ID to retrieve
 * @returns Package details
 * @throws Error if package not found or authorization fails
 */
export async function getPackageAsync(packageId: string): Promise<LicensePackageDetails> {
  if (!packageId) {
    throw new Error("Package ID is required");
  }

  return orderApiFetch<LicensePackageDetails>(
    `/packages/${encodeURIComponent(packageId)}`,
    { method: "GET" }
  );
}

/**
 * Create a new license package (hosting operator only).
 * @param request - Package creation details
 * @returns Created package
 * @throws Error if authorization fails or request validation fails
 */
export async function createPackageAsync(request: CreateOrUpdatePackageRequest): Promise<LicensePackageDetails> {
  if (!request.name) {
    throw new Error("Package name is required");
  }

  return orderApiFetch<LicensePackageDetails>(
    '/portal/packages',
    {
      method: "POST",
      body: JSON.stringify(request)
    }
  );
}

/**
 * Update an existing license package (hosting operator only).
 * @param packageId - Package ID to update
 * @param request - Updated package details
 * @returns Updated package
 * @throws Error if authorization fails or request validation fails
 */
export async function updatePackageAsync(
  packageId: string,
  request: CreateOrUpdatePackageRequest
): Promise<LicensePackageDetails> {
  if (!packageId) {
    throw new Error("Package ID is required");
  }
  if (!request.name) {
    throw new Error("Package name is required");
  }

  return orderApiFetch<LicensePackageDetails>(
    `/portal/packages/${encodeURIComponent(packageId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(request)
    }
  );
}
